import {
  closestTo,
  differenceInDays,
  getMonth,
  isBefore,
  isWithinInterval
} from 'date-fns';
import { filter, map } from 'lodash';
import { isSeasonPeriod, Period, SeasonCalendar } from 'types/Product';
import {
  dayOfYear,
  daysUntil,
  formatDate,
  isAfterThisYear,
  isBeforeThisYear,
  maxCurrentYear,
  minCurrentYear,
  parseDate
} from 'utils/date/format';

export const isAlwaysInSeason = (
  seasonCalendar: SeasonCalendar = []
): boolean => {
  if (seasonCalendar.length !== 1) {
    return false;
  }
  const firstSeason = seasonCalendar[0];
  if (isSeasonPeriod(firstSeason)) {
    return false;
  }
  return daysUntil(firstSeason.from) <= 0;
};

const validatePeriod = ({ from, to }: Partial<Period>): boolean => {
  return Boolean(from) && Boolean(to);
};

export const isValidThisYear = (period: Period): boolean => {
  return (
    validatePeriod(period) &&
    (!period.to || !isBeforeThisYear(period.to)) &&
    !isAfterThisYear(period.from)
  );
};

export const calculatePeriodPosition = ({ from, to = '' }: Period) => {
  const fromDayOfYear = dayOfYear(minCurrentYear(from));
  const toDayOfYear = dayOfYear(maxCurrentYear(to));
  const numDaysInPeriod = toDayOfYear - fromDayOfYear;

  return {
    left: `${(fromDayOfYear / 365) * 100}%`,
    width: `${(numDaysInPeriod / 365) * 100}%`
  };
};

const isInSeason = (seasonCalendar: SeasonCalendar = []): boolean => {
  const today = new Date();
  return seasonCalendar.some(season => {
    if (!validatePeriod(season) || !isSeasonPeriod(season)) return false;
    return isWithinInterval(today, {
      start: parseDate(season.from),
      end: parseDate(season.to)
    });
  });
};

const getFirstSeasonStart = (
  seasonCalendar: SeasonCalendar = [],
  today: Date
) => {
  const futureSeasonStarts = filter(
    map(seasonCalendar, ({ from }) => {
      return parseDate(from);
    }),
    fromDate => {
      return !isBefore(fromDate, today);
    }
  );

  return closestTo(today, futureSeasonStarts);
};

export const getUpcomingSeason = (
  seasonCalendar: SeasonCalendar = []
): { diff: number; month: string } | false => {
  if (
    seasonCalendar.length === 0 ||
    isInSeason(seasonCalendar) ||
    isAlwaysInSeason(seasonCalendar)
  )
    return false;

  const today = new Date();

  const firstSeasonStart = getFirstSeasonStart(seasonCalendar, today);

  if (!firstSeasonStart || differenceInDays(firstSeasonStart, today) < 14)
    return false;

  return {
    diff: Math.abs(getMonth(today) - getMonth(firstSeasonStart)),
    month: formatDate(firstSeasonStart, 'MMMM')
  };
};

export const daysUntilAvailabilityChanges = (today = new Date()) => {
  return ({
    seasonCalendar = []
  }: {
    seasonCalendar: SeasonCalendar;
  }): number => {
    if (isAlwaysInSeason(seasonCalendar)) {
      return Infinity;
    }
    const dates = seasonCalendar.flatMap(period => {
      const { from } = period;
      const to = 'to' in period ? period.to : '';
      return [parseDate(to), parseDate(from)];
    });
    const closestDate = closestTo(today, dates);
    if (!closestDate) {
      return Infinity;
    }
    return Math.abs(differenceInDays(today, closestDate));
  };
};

export const formatPeriod = (period?: Partial<Period>) => {
  const { from, to } = period ?? {};
  return [
    from && formatDate(from, 'dd.MM.yy'),
    to && formatDate(to, 'dd.MM.yy')
  ]
    .filter(Boolean)
    .join(' - ');
};
